import { useState } from "react"
import { Link } from "react-router-dom"
import { UserRole } from "~/__generated__/graphql"
import { useLogout } from "~/auth/use-logout"
import { useViewerMaybe } from "~/auth/use-viewer"
import {
  adminBookmarksPath,
  bookmarksPath,
  groupsPath,
  historyPath,
  profilePath,
} from "~/common/paths"
import bookmarkIcon from "~/images/bookmark-icon"
import closeIcon from "~/images/close"
import groupsIcon from "~/images/groups-icon"
import hamburgerIcon from "~/images/hamburger"
import logoIcon from "~/images/logo-icon"
import logoIconGray from "~/images/logo-icon-gray"
import { UserAvatar } from "~/users/user-avatar"
import LoggedOutHeader from "./logged-out-header"
import FlashMessage from "~/components/flash-message"

const LoggedInHeader = () => {
  const { viewer } = useViewerMaybe()
  const logout = useLogout()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const closeMobileMenu = () => setMobileMenuOpen(false)

  if (!viewer) {
    return <LoggedOutHeader />
  }

  return (
    <>
      <header className="sticky top-0 z-50 bg-white">
        <div className="border-b border-gray-300">
          <div className="mx-auto px-4">
            <div className="flex h-16 items-center justify-between">
              <div className="flex items-center">
                {/* Mobile Menu Button */}
                <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                  <img
                    {...(mobileMenuOpen ? closeIcon : hamburgerIcon)}
                    alt={mobileMenuOpen ? "Close menu" : "Open menu"}
                    className="h-4 w-4"
                  />
                </button>

                {/* Logo and App Name */}
                <Link className="ml-4 flex items-center" to={bookmarksPath({})}>
                  <img {...logoIcon} alt="Logo" className="h-4 w-4" />
                  <span className="ml-4 font-bold">Tasti</span>
                </Link>

                {/* Desktop Navigation */}
                <nav className="ml-6 hidden items-center space-x-6 md:flex">
                  <Link to={bookmarksPath({})} className="flex items-center">
                    <img {...bookmarkIcon} alt="Bookmarks" className="h-4 w-4" />
                    <span className="ml-4">Bookmarks</span>
                  </Link>
                  <Link to={historyPath({})} className="flex items-center">
                    <img {...logoIconGray} alt="History" className="h-4 w-4" />
                    <span className="ml-4">History</span>
                  </Link>
                  <Link to={groupsPath({})} className="flex items-center">
                    <img {...groupsIcon} alt="Groups" className="h-4 w-4" />
                    <span className="ml-4">Groups</span>
                  </Link>
                  {viewer.userRole === UserRole.SystemAdmin && (
                    <>
                      <Link to={adminBookmarksPath({})} className="flex items-center">
                        <div className="h-4 w-4"></div>
                        <span className="ml-4">Admin</span>
                      </Link>
                    </>
                  )}
                </nav>
              </div>

              {/* Right Side */}
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  className="flex items-center space-x-2"
                >
                  <UserAvatar user={viewer} alt={`${viewer.fullName}'s Avatar`} />
                  <span className="hidden sm:inline">{viewer.fullName}</span>
                </button>

                {/* User Dropdown Menu */}
                {dropdownOpen && (
                  <div className="absolute right-0 z-10 mt-2 w-48 rounded-md bg-white text-gray-800 shadow-lg">
                    <Link
                      onClick={() => setDropdownOpen(false)}
                      to={profilePath({})}
                      className="block px-4 py-2"
                    >
                      My Profile
                    </Link>
                    <button onClick={logout} className="block w-full px-4 py-2 text-left">
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden">
            <nav className="flex flex-col">
              <Link
                to={bookmarksPath({})}
                className="w-full border-b border-gray-200"
                onClick={closeMobileMenu}
              >
                <div className="mx-auto px-4">
                  <div className="flex items-center py-3">
                    <img {...bookmarkIcon} alt="Bookmarks" className="h-4 w-4" />
                    <span className="ml-4">Bookmarks</span>
                  </div>
                </div>
              </Link>
              <Link
                to={historyPath({})}
                className="w-full border-b border-gray-200"
                onClick={closeMobileMenu}
              >
                <div className="mx-auto px-4">
                  <div className="flex items-center py-3">
                    <img {...logoIconGray} alt="History" className="h-4 w-4" />
                    <span className="ml-4">History</span>
                  </div>
                </div>
              </Link>
              <Link
                to={groupsPath({})}
                className="w-full border-b border-gray-200"
                onClick={closeMobileMenu}
              >
                <div className="mx-auto px-4">
                  <div className="flex items-center py-3">
                    <img {...groupsIcon} alt="Groups" className="h-4 w-4" />
                    <span className="ml-4">Groups</span>
                  </div>
                </div>
              </Link>
              {viewer.userRole === UserRole.SystemAdmin && (
                <Link
                  to={adminBookmarksPath({})}
                  className="w-full border-b border-gray-200"
                  onClick={closeMobileMenu}
                >
                  <div className="mx-auto px-4">
                    <div className="flex items-center py-3">
                      <div className="h-4 w-4"></div>
                      <span className="ml-4">Admin</span>
                    </div>
                  </div>
                </Link>
              )}
            </nav>
          </div>
        )}
      </header>
      <FlashMessage />
    </>
  )
}

export default LoggedInHeader
